import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	type Establishment,
} from "@pilatestime/shared-types";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
	useCreateEstablishment,
	useDeleteEstablishment,
	useUpdateEstablishment,
} from "@/api/establishments";
import { ConfirmationWarningModal } from "@/components/ConfirmationWarningModal";
import type { PaginationMeta } from "@/components/data-table";
import {
	ListPageCard,
	ListPageCardContent,
	ListPageCardHeader,
} from "@/components/ListPageCard";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { useCan } from "@/hooks/useAccessControl";
import { useApiParameters } from "@/hooks/useApiParameters";
import {
	EstablishmentFormModal,
	type EstablishmentFormValues,
} from "./-components/EstablishmentFormModal";
import { EstablishmentList } from "./-components/EstablishmentList";

/** "create" opens a blank modal; an establishment opens it seeded for edit. */
type FormTarget = "create" | Establishment | null;

const searchSchema = z.object({
	page: z.number().catch(DEFAULT_PAGE),
	limit: z.number().catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/establishments/",
)({
	validateSearch: searchSchema,
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const orgId = params.orgId;

		return { userId, token, orgId };
	},
	component: EstablishmentsPage,
});

function EstablishmentsPage() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: "/_auth/app/_app/org/$orgId/establishments/",
	});
	const search = Route.useSearch();
	const { userId, token } = Route.useLoaderData();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 500); // 500ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const can = useCan();
	const canManageEstablishments = can(Action.MANAGE_ESTABLISHMENTS);

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	// State for the create/edit modal and the delete confirmation.
	const [formTarget, setFormTarget] = useState<FormTarget>(null);
	const [establishmentToDelete, setEstablishmentToDelete] =
		useState<Establishment | null>(null);

	// Mutations
	const createEstablishmentMutation = useCreateEstablishment();
	const updateEstablishmentMutation = useUpdateEstablishment();
	const deleteEstablishmentMutation = useDeleteEstablishment();

	const editingEstablishment = formTarget === "create" ? null : formTarget;

	const handleSubmit = async (values: EstablishmentFormValues) => {
		try {
			if (editingEstablishment) {
				await updateEstablishmentMutation.mutateAsync({
					token,
					organizationId: orgId,
					establishmentId: editingEstablishment.id,
					data: values,
				});
			} else {
				await createEstablishmentMutation.mutateAsync({
					token,
					organizationId: orgId,
					data: values,
				});
			}
			// Success toast is handled by the mutation hook.
			setFormTarget(null);
		} catch (error) {
			// Error toast is handled by the mutation hook; the modal stays open so
			// the admin keeps everything they typed and can retry.
			console.error("Failed to save establishment:", error);
		}
	};

	// Delete lives in the edit modal action bar: opening the confirm closes the
	// edit modal so delete has one obvious home per row and avoids stacked modals.
	const handleRequestDelete = () => {
		if (!editingEstablishment) return;
		setEstablishmentToDelete(editingEstablishment);
		setFormTarget(null);
	};

	const handleConfirmDelete = async () => {
		if (!establishmentToDelete) return;

		try {
			await deleteEstablishmentMutation.mutateAsync({
				token,
				organizationId: orgId,
				establishmentId: establishmentToDelete.id,
			});
			setEstablishmentToDelete(null);
		} catch (error) {
			// Error toast is handled by the mutation hook
			console.error("Failed to delete establishment:", error);
		}
	};

	const isLoading =
		createEstablishmentMutation.isPending ||
		updateEstablishmentMutation.isPending ||
		deleteEstablishmentMutation.isPending;

	return (
		<div
			className="h-full flex flex-col space-y-2 sm:space-y-3"
			data-testid="establishments-page"
		>
			{/* Header */}
			<PageHeader
				title={t("establishments.management.title")}
				description={t("establishments.management.description")}
				showBackButton={false}
			/>
			{/* Establishments Card */}
			<ListPageCard>
				<ListPageCardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
					{/* Search */}
					<div className="relative w-full sm:w-72">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("establishments.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="establishments-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						{searchTerm !== debouncedSearchTerm && (
							<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							</div>
						)}
					</div>

					{/* Create Button */}
					{canManageEstablishments && (
						<Button
							className="btn btn-primary text-primary-content w-full sm:w-auto"
							onClick={() => setFormTarget("create")}
							data-testid="create-establishment-button"
						>
							<Plus className="h-4 w-4 mr-2" />
							{t("establishments.management.createNew")}
						</Button>
					)}
				</ListPageCardHeader>

				<ListPageCardContent>
					{/* Table */}
					<div className="flex-1 overflow-hidden">
						<EstablishmentList
							organizationId={orgId}
							pagination={{
								page: currentPage,
								limit: pageSize,
							}}
							searchTerm={debouncedSearchTerm || undefined}
							userId={userId}
							token={token}
							canManageEstablishments={canManageEstablishments || false}
							onPaginationMetaChange={handlePaginationMetaChange}
							onEdit={(establishment) => setFormTarget(establishment)}
							onCreate={() => setFormTarget("create")}
						/>
					</div>

					{/* Pagination */}
					<div className="flex-shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>
				</ListPageCardContent>
			</ListPageCard>

			{/* Create / Edit form. Keyed so each target seeds its own state. */}
			{formTarget !== null && (
				<EstablishmentFormModal
					key={formTarget === "create" ? "create" : formTarget.id}
					open
					onOpenChange={(open) => {
						if (!open) setFormTarget(null);
					}}
					establishment={editingEstablishment}
					isSubmitting={isLoading}
					onSubmit={handleSubmit}
					onDelete={
						editingEstablishment && canManageEstablishments
							? handleRequestDelete
							: undefined
					}
				/>
			)}

			{/* Delete Confirmation */}
			<ConfirmationWarningModal
				isOpen={!!establishmentToDelete}
				onClose={() => setEstablishmentToDelete(null)}
				onConfirm={handleConfirmDelete}
				title={t("establishments.management.actions.confirmDelete")}
				subtitle={
					establishmentToDelete?.name ??
					t("establishments.management.actions.confirmDeleteDescription")
				}
				warningTitle={t("establishments.management.actions.confirmDelete")}
				warningMessage={t(
					"establishments.management.actions.confirmDeleteWarning",
				)}
				confirmButtonText={t("establishments.management.actions.delete")}
				loadingText={t("common.deleting")}
				isLoading={deleteEstablishmentMutation.isPending}
				variant="danger"
			/>
		</div>
	);
}
